/**
 * Powerbanks statuses donut (ChartUserByCountry style).
 * Donut with center label + list with LinearProgress per status.
 */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import { PieChart } from '@mui/x-charts/PieChart';
import { useDrawingArea } from '@mui/x-charts/hooks';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

const STATUS_KEYS = [
  'inStation',
  'inRent',
  'atAdmin',
  'atMerchant',
  'atSuperAdmin',
  'lost',
];

const LABEL_KEYS = {
  inStation: 'powerbanksInStation',
  inRent: 'powerbanksInRent',
  atAdmin: 'powerbanksAtAdmin',
  atMerchant: 'powerbanksAtMerchant',
  atSuperAdmin: 'powerbanksAtSuperAdmin',
  lost: 'powerbanksLost',
};

const StyledText = styled('text', {
  shouldForwardProp: (prop) => prop !== 'variant',
})(({ theme }) => ({
  textAnchor: 'middle',
  dominantBaseline: 'central',
  fill: (theme.vars || theme).palette.text.secondary,
  variants: [
    {
      props: { variant: 'primary' },
      style: { fontSize: theme.typography.h5.fontSize, fontWeight: theme.typography.h5.fontWeight },
    },
    {
      props: ({ variant }) => variant !== 'primary',
      style: { fontSize: theme.typography.body2.fontSize, fontWeight: theme.typography.body2.fontWeight },
    },
  ],
}));

function PieCenterLabel({ primaryText, secondaryText }) {
  const { width, height, left, top } = useDrawingArea();
  const primaryY = top + height / 2 - 10;
  const secondaryY = primaryY + 24;
  return (
    <React.Fragment>
      <StyledText variant="primary" x={left + width / 2} y={primaryY}>
        {primaryText}
      </StyledText>
      <StyledText variant="secondary" x={left + width / 2} y={secondaryY}>
        {secondaryText}
      </StyledText>
    </React.Fragment>
  );
}

PieCenterLabel.propTypes = {
  primaryText: PropTypes.string.isRequired,
  secondaryText: PropTypes.string.isRequired,
};

export default function PowerbanksStatusesDonut({ dataStatuses, title = 'Powerbanks statuses' }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const palette = [
    theme.palette.primary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.info.main,
    theme.palette.secondary.main,
    theme.palette.error.main,
  ];

  const chartData = useMemo(() => {
    const getLabel = (key) => t(`dashboard.${LABEL_KEYS[key]}`);
    if (!dataStatuses || typeof dataStatuses.inStation === 'undefined') {
      return STATUS_KEYS.map((key, i) => ({ id: i, value: 0, label: getLabel(key) }));
    }
    return STATUS_KEYS.map((key, i) => ({
      id: i,
      value: dataStatuses[key] ?? 0,
      label: getLabel(key),
    })).filter((d) => d.value > 0);
  }, [dataStatuses, t]);

  const total = useMemo(
    () => chartData.reduce((s, d) => s + d.value, 0),
    [chartData],
  );

  const listItems = useMemo(() => {
    if (!dataStatuses || total === 0) return [];
    return STATUS_KEYS.map((key, i) => {
      const value = Number(dataStatuses[key]) || 0;
      const percent = total > 0 ? Math.round((value / total) * 100) : 0;
      return {
        key,
        name: t(`dashboard.${LABEL_KEYS[key]}`),
        value,
        percent,
        color: palette[i],
      };
    }).filter((item) => item.value > 0);
  }, [dataStatuses, total, palette, t]);

  const pieSeriesData = chartData.length > 0 ? chartData : [{ id: 0, value: 1, label: '—' }];

  return (
    <Card variant="outlined" sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flexGrow: 1 }}>
      <CardContent>
        <Typography component="h2" variant="subtitle2" gutterBottom>
          {title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PieChart
            colors={palette}
            margin={{ left: 80, right: 80, top: 80, bottom: 80 }}
            series={[
              {
                data: pieSeriesData,
                innerRadius: 75,
                outerRadius: 100,
                paddingAngle: 0,
                highlightScope: { fade: 'global', highlight: 'item' },
              },
            ]}
            height={260}
            width={260}
            hideLegend
          >
            <PieCenterLabel primaryText={String(total)} secondaryText={t('dashboard.powerbanksTotal')} />
          </PieChart>
        </Box>
        {listItems.map((item) => (
          <Stack key={item.key} direction="row" sx={{ alignItems: 'center', gap: 2, pb: 2 }}>
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                bgcolor: item.color,
                flexShrink: 0,
              }}
            />
            <Stack sx={{ gap: 1, flexGrow: 1 }}>
              <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: '500' }}>
                  {item.name}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {item.value} ({item.percent}%)
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                aria-label={item.name}
                value={Math.min(100, item.percent)}
                sx={{
                  [`& .${linearProgressClasses.bar}`]: {
                    backgroundColor: item.color,
                  },
                }}
              />
            </Stack>
          </Stack>
        ))}
      </CardContent>
    </Card>
  );
}

PowerbanksStatusesDonut.propTypes = {
  dataStatuses: PropTypes.shape({
    inStation: PropTypes.number,
    inRent: PropTypes.number,
    atAdmin: PropTypes.number,
    atMerchant: PropTypes.number,
    atSuperAdmin: PropTypes.number,
    lost: PropTypes.number,
  }),
  title: PropTypes.string,
};
